import React, {useContext, useEffect, useState, useRef, SyntheticEvent } from 'react';
import AppLayout from 'components/AppLayout/AppLayout';
import AppDrawer from 'components/AppDrawer/AppDrawer';
import Typography from '@mui/material/Typography';
import GppMaybe from '@mui/icons-material/GppMaybe';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Item from '@mui/material/Grid';
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Info from "@mui/icons-material/Info";
import Alert from '@mui/material/Alert';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";
import Checkbox from '@mui/material/Checkbox';

import TextField from '@mui/material/TextField';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import "i18n/config";
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify'
import { DataContext } from 'contexts/DataContext';
import { get, update, getControls, saveTreatments, autoTreatments, approve} from './api';
import { get as getRisks } from 'pages/Risks/api';
import RiskChart from './RiskChart';
import Gauge from './Gauge';
import OutlinedInput from '@mui/material/OutlinedInput';
import { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import { get as getTreatments} from 'pages/Treatments/api';
import { Link } from "react-router-dom";

const Strategy = () => {
    const [strategy, setStrategy] = useState({} as any);
    const [saving, setSaving] = useState(false);
    const [maturities, setMaturities] = useState([] as any);
    const [coverages, setCoverages] = useState([] as any);
    const [id_maturity, setIdMaturity] = useState('');
    const [id_coverage, setIdCoverage] = useState('');
    const [controls, setControls] = useState([] as any);
    const [allControls, setAllControls] = useState([] as any);
    const [status, setStatus] = useState({} as any);
    const [target, setTarget] = useState({} as any);
    const [missingTreatment, setMissingTreatment] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    
    const [treatmentName, setTreatmentName] = useState<string[]>([]);
    const [treatments, setTreatments] = useState([] as any);
    const [treatmentsList, setTreatmentsList] = useState([] as any);
    
    const [effectiveRisk, setEffectiveRisk] = useState(1);
    const [residualRisk, setResidualRisk] = useState(1);

    const [searchControl, setSearchControl] = useState('' as any);
    const [searchCoverage, setSearchCoverage] = useState('' as any);
    const [searchMaturity, setSearchMaturity] = useState('' as any);
    const [searchTreatments, setSearchTreatments] = useState('' as any);

    const dataContext = useContext(DataContext);
    const formRef = useRef<HTMLFormElement>(null);
    const { t } = useTranslation();


    const handleChange = (event: SelectChangeEvent<typeof treatmentName>) => {
        const {
          target: { name, value },
        } = event;

        let treatmentNames = [] as string[];
        treatmentNames[name] = typeof value === 'string' ? value.split(',') : value;
        treatments[name] = treatmentNames[name];
        setTreatments(treatments);

        saveTreatments(treatments[name],dataContext.currentAssessment, name).then(response=>{
            setMissingTreatment(response.filter((control:any)=>{
                return control.treatment?.length===0
            }).length);
        })

        setTreatmentName(
          // On autofill we get a stringified value.
          typeof value === 'string' ? value.split(',') : value,
        );
      };

      
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        dataContext.changeShowLoader(true);
        const data = new FormData(event.currentTarget);
        update(data, dataContext.currentAssessment, strategy.id).then((response) => {
            setStrategy(response.strategy);
            setStatus(response.total);
            setTarget(response.target);
            toast.success('Strategy saved', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                });
            
            setSaving(false);
            dataContext.changeShowLoader(false);
        });
    };

    useEffect(() => {
        dataContext.changeShowLoader(true);
        if(dataContext.loading) return;
        if(!dataContext.currentAssessment) return;
        get(dataContext.currentAssessment).then((response) => {
            setStrategy(response.strategy);
            setStatus(response.total);
            setTarget(response.target);

            getRisks(dataContext.currentAssessment,strategy.id).then((response)=>{
                let effectiveRisk = response.map((risks:any)=>risks.max_effective_risks_details);
                effectiveRisk = Math.max(...effectiveRisk) < 1 ? 1 : Math.max(...effectiveRisk);
                //console.log("EFFECTIVE:"+effectiveRisk)
                setEffectiveRisk(effectiveRisk);
                let residualRisk = response.map((risks:any)=>risks.max_residual_risks_details);
                residualRisk = Math.max(...residualRisk) < 1 ? 1 : Math.max(...residualRisk);
                //console.log("RESIDUAL:"+residualRisk)
                setResidualRisk(isNaN(residualRisk) ? effectiveRisk : residualRisk);
                dataContext.changeShowLoader(false);
            })
            
        });
        setCoverages(dataContext.coverages);
        setMaturities(dataContext.maturities);
        //eslint-disable-next-line
    }, [dataContext.loading, dataContext.currentAssessment, strategy.id]);

    useEffect(() => {
        if(Object.keys(strategy).length===0) return;
        if(!strategy.id) return;
        dataContext.changeShowLoader(true);

        getRisks(dataContext.currentAssessment,strategy.id).then((response)=>{
            let effectiveRisk = response.map((risks:any)=>risks.max_effective_risks_details);
            effectiveRisk = Math.max(...effectiveRisk) < 1 ? 1 : Math.max(...effectiveRisk);
            setEffectiveRisk(effectiveRisk);
            let residualRisk = response.map((risks:any)=>risks.max_residual_risks_details);
            residualRisk = Math.max(...residualRisk) < 1 ? 1 : Math.max(...residualRisk);
            setResidualRisk(isNaN(residualRisk) ? effectiveRisk : residualRisk);
            //console.log(residualRisk);
            //setResidualRisk(residualRisk);
        })

        getTreatments(dataContext.currentAssessment).then(response=>{
            setTreatmentsList(response);
        })

        getControls(dataContext.currentAssessment).then((response)=>{
            
            //mostro solo i controlli univoci
            setControls(response.filter((control:any, index:number, self:any) =>
                index === self.findIndex((c:any) => c.id_control === control.id_control)
            ));
            setAllControls(response.filter((control:any, index:number, self:any) =>
                index === self.findIndex((c:any) => c.id_control === control.id_control)
            ));

            response.map((control:any)=>{
                treatments[control.id_control] = control.treatment?.map((treatment:any)=>treatment.id+"-"+treatment.name);
                setTreatments(treatments);
                return 0;
            })

            setMissingTreatment(response.filter((control:any)=>{
                return control.treatment?.length===0
            }).length);

            dataContext.changeShowLoader(false);
        })
        //eslint-disable-next-line
    }, [strategy]);

    useEffect(() => {
        let filteredControls = allControls;
        if(searchControl) {
            filteredControls = filteredControls.filter((control:any) => 
                control.id_control.toLowerCase().includes(searchControl.toLowerCase()) || 
                control.control_description.toLowerCase().includes(searchControl.toLowerCase())
            );
        }

        if (searchCoverage) {
            filteredControls = filteredControls.filter((control:any) => control.id_coverage===searchCoverage);
        }
    
        if (searchMaturity) {
            filteredControls = filteredControls.filter((control:any) => control.id_maturity===searchMaturity);
        }

        if (searchTreatments) {
            filteredControls = filteredControls.filter((control:any) => control.treatment?.find((treatment:any) => treatment.id === searchTreatments));
        }

        setControls(filteredControls);

        //eslint-disable-next-line
    },[searchControl,searchCoverage,searchMaturity,searchTreatments]);


    const coverageValue = id_coverage
      ? id_coverage
      : (strategy.target_coverage ? String(strategy.target_coverage) : '');
    const maturityValue = id_maturity
      ? id_maturity
      : (strategy.target_maturity ? String(strategy.target_maturity) : '');

    const handleAutoTreatments = () => {
        dataContext.changeShowLoader(true);
                    autoTreatments(dataContext.currentAssessment).then(response=>{
                        dataContext.changeShowLoader(false);
                        setConfirmOpen(false);
                        toast.success('Treatment assigned.', {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            });

                        const uniqueControls = response.filter((control:any, index:number, self:any) =>
                            index === self.findIndex((c:any) => c.id_control === control.id_control)
                        );
                        setControls(uniqueControls);
                        setAllControls(uniqueControls);
                        const newTreatments = {...treatments};
                        uniqueControls.map((control:any)=>{
                            newTreatments[control.id_control] = control.treatment?.map((treatment:any)=>treatment.id+"-"+treatment.name);
                            return 0;
                        })
                        setTreatments(newTreatments);
            
                        setMissingTreatment(uniqueControls.filter((control:any)=>{
                            return control.treatment?.length===0
                        }).length);
                    });
    }
    return (
        <AppLayout drawerOpen={true} drawer={<AppDrawer />} 
        title={<><Typography variant='pageTitle'>Strategy</Typography></>} 
        actions={dataContext.user?.id_role===1 ? [] :
            strategy.id && !strategy.approved ? [{
                variant: 'outlined',
                label: <><AutoFixHighIcon /></>,
                disabled: saving ? true : false,
                action: (event: SyntheticEvent) => {
                    setConfirmOpen(true);
                },

            },
            {
                variant: 'outlined',
                label: saving ? t('Saving...') : strategy.approved ? t('Unapprove'): t('Approve'),
                disabled: saving ? true : false,
                action: (event: SyntheticEvent) => {
                    approve(strategy.id).then(response=>{
                        setStrategy(response);
                    })
                }
            },
            {
                label: saving ? t('Saving...') : t('Save'),
                disabled: saving ? true : false,
                action: (event: SyntheticEvent) => {
                    setSaving(true);
                    formRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }
            }] : 
            strategy.approved ? [
                {
                    variant: 'outlined',
                    label: saving ? t('Saving...') : strategy.approved ? t('Unapprove'): t('Approve'),
                    disabled: saving ? true : false,
                    action: (event: SyntheticEvent) => {
                        approve(strategy.id).then(response=>{
                            setStrategy(response);
                        })
                    }
                }
            ] : [
                {
                    label: saving ? t('Saving...') : t('Save'),
                    disabled: saving ? true : false,
                    action: (event: SyntheticEvent) => {
                        setSaving(true);
                        formRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                    }
                }
            ]
        }>
        <Box ref={formRef} component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 0 }}>
        {missingTreatment>0 && <Alert sx={{marginBottom:1}} severity="warning">{missingTreatment + " " + t('controls are missing treatments.')}</Alert>}

        <Dialog
            open={confirmOpen}
            onClose={()=>setConfirmOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
        <DialogTitle id="alert-dialog-title">
            {t('Confirm auto assignment?')}
            </DialogTitle>
            <DialogContent>
            <DialogContentText id="alert-dialog-description">
                {t('Treatments will be reassigned automatically starting from the target coverage and maturity.')}
            </DialogContentText>
            <Alert severity="warning" sx={{marginTop:2}}>
                {treatmentsList.length > 0
                    ? t('The current plan will be deleted: {{count}} treatments with their dates, budgets and owners.').replace('{{count}}', String(treatmentsList.length))
                    : t('The current plan will be deleted.')}
                {' '}
                {t('The new treatments will be recreated with default dates and no budget.')}
            </Alert>
            </DialogContent>
            <DialogActions>
            <Button onClick={()=>setConfirmOpen(false)} autoFocus>{t('Cancel')}</Button>
            <Button color="error" onClick={()=>{handleAutoTreatments()}}>
                {t('Delete and reassign')}
            </Button>
            </DialogActions>
        </Dialog>


        <Paper sx={{marginTop:1, padding:1}}>
            <Box sx={{margin:1}}>
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={6}>
                        <Item>
                        <FormControl disabled={strategy.approved===1 ? true : false} sx={{minWidth:100, marginTop: 2, marginRight:2}} fullWidth>
                            <InputLabel id="coverage-select-label">{t("Target coverage")}</InputLabel>
                            <Select
                                key={id_coverage}
                                startAdornment={
                                (id_coverage ? id_coverage : strategy.target_coverage) ?
                                <InputAdornment position="start">
                                    <Tooltip title= {
                                        (coverages.find((coverageRow: any) => coverageRow.id === (id_coverage ? id_coverage : strategy.target_coverage)))?.description ?? 'Choose a coverage value'
                                        }>
                                        <Info />
                                    </Tooltip>
                                </InputAdornment>
                                : null}
                            labelId="coverage-select-label"
                            label={t("Target coverage")}
                            value={coverageValue}
                            onChange={(e) => setIdCoverage(e.target.value)}
                            name="target_coverage"
                            sx={(theme) => ({
                              '& .MuiSelect-select': {
                                fontSize: coverageValue === '' ? 16 : 14,
                                color: coverageValue === ''
                                  ? theme.palette.text.secondary
                                  : 'inherit',
                              },
                            })}
                            >
                            {coverages.map((coverage: any, index: number) => {
                                return (
                                    <MenuItem key={index} value={coverage.id}>{coverage.name}</MenuItem>
                                )
                            })}
                            </Select>
                        </FormControl>
                        </Item>
                    </Grid>
                    <Grid item xs={6}>
                        <Item>
                        <FormControl disabled={strategy.approved===1 ? true : false} sx={{minWidth:100, marginTop: 2, marginRight:2}} fullWidth>
                            <InputLabel id="maturity-select-label">{t("Target maturity")}</InputLabel>
                            <Select
                                key={id_maturity}
                                startAdornment={
                                (id_maturity ? id_maturity : strategy.target_maturity) ?
                                <InputAdornment position="start">
                                    <Tooltip title= {
                                        (maturities.find((maturityRow: any) => maturityRow.id === (id_maturity ? id_maturity : strategy.target_maturity)))?.description ?? 'Choose a maturity value'
                                        }>
                                        <Info />
                                    </Tooltip>
                                </InputAdornment>
                                : null}
                            labelId="maturity-select-label"
                            label={t("Target maturity")}
                            value={maturityValue}
                            onChange={(e) => setIdMaturity(e.target.value)}
                            name="target_maturity"
                            sx={(theme) => ({
                              '& .MuiSelect-select': {
                                fontSize: maturityValue === '' ? 16 : 14,
                                color: maturityValue === ''
                                  ? theme.palette.text.secondary
                                  : 'inherit',
                              },
                            })}
                            >
                            {maturities.map((maturity: any, index: number) => {
                                return (
                                    <MenuItem key={index} value={maturity.id}>{maturity.name}</MenuItem>
                                )
                            })}
                            </Select>
                        </FormControl>
                        </Item>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
        <Paper sx={{marginTop:1, padding:1}}>
            <Box sx={{margin:1}}>
                <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={3}>
                        <Item>
                        <Typography variant='paperTitle'>{t('Effective risk')}</Typography>

                            <RiskChart key={effectiveRisk} name='effectiveRisk' value={effectiveRisk}></RiskChart>

                        </Item>
                    </Grid>
                    <Grid item xs={3}>
                        <Item>
                        <Typography variant='paperTitle'>{t('Residual risk')}</Typography>

                            <RiskChart key={residualRisk} name='residualRisk' value={residualRisk}></RiskChart>

                        </Item>
                    </Grid>
                    <Grid item xs={3}>
                        <Item>
                        {coverages.length>0 &&
                            <div>
                            <Typography variant='paperTitle'>{t('Coverage')}</Typography>
                            {status.score!==false && 
                            <Gauge key={strategy.target_coverage ?? target.score} name='coverage' value={target.score} backgroundValue={status.score}></Gauge>}
                            
                        </div>}
                        </Item>
                    </Grid>
                    <Grid item xs={3}>
                        <Item>
                        {maturities.length>0 && 
                            <div>
                            <Typography variant='paperTitle'>{t('Maturity')}</Typography>
                            {target?.maturity!==false && 
                            <Gauge key={strategy.target_maturity ?? target.maturity} name='maturity' value={target.maturity} backgroundValue={status.maturity}></Gauge>}

                            
                        </div>}
                        </Item>
                    </Grid>
                    
                </Grid>
            </Box>
        </Paper>
        {Object.keys(allControls).length>0 && 
        <Paper sx={{marginTop:1, padding:1}} key={strategy.target_coverage+""+strategy.target_maturity}>
            <Box sx={{margin:1}}>
                <Typography variant='paperTitle'>Controls ({Object.keys(controls).length})</Typography>
       


            <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650, marginTop: 2 }} size="small" aria-label="a dense table">
  <TableHead>
  <TableRow>
    {/* Control */}
    <TableCell>
      <FormControl fullWidth size="small">
        <InputLabel id="control-filter-label">{t("Control")}</InputLabel>
        <TextField
          id="control-filter"
          variant="outlined"
          size="small"
          onChange={(e) => setSearchControl(e.target.value)}
        />
      </FormControl>
    </TableCell>

    {/* Coverage */}
    <TableCell>
      <FormControl fullWidth size="small">
        <InputLabel id="coverage-filter-label">{t("Coverage")}</InputLabel>
        <Select
          labelId="coverage-filter-label"
          label={t("Coverage")}
          value={searchCoverage}
          onChange={(e) => setSearchCoverage(e.target.value)}
        >
          <MenuItem value="">
            <em>&nbsp;</em>
          </MenuItem>
          {coverages.map((coverage: any, index: number) => (
            <MenuItem key={index} value={coverage.id}>
              {coverage.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </TableCell>

    {/* Maturity */}
    <TableCell>
      <FormControl fullWidth size="small">
        <InputLabel id="maturity-filter-label">{t("Maturity")}</InputLabel>
        <Select
          labelId="maturity-filter-label"
          label={t("Maturity")}
          value={searchMaturity}
          onChange={(e) => setSearchMaturity(e.target.value)}
        >
          <MenuItem value="">
            <em>&nbsp;</em>
          </MenuItem>
          {maturities.map((maturity: any, index: number) => (
            <MenuItem key={index} value={maturity.id}>
              {maturity.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </TableCell>

    {/* Treatments */}
    <TableCell>
      <FormControl fullWidth size="small">
        <InputLabel id="treatments-filter-label">{t("Treatments")}</InputLabel>
        <Select
          labelId="treatments-filter-label"
          label={t("Treatments")}
          value={searchTreatments}
          onChange={(e) => setSearchTreatments(e.target.value)}
        >
          <MenuItem value="">
            <em>&nbsp;</em>
          </MenuItem>
          {treatmentsList
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((treatment: any, index: number) => (
              <MenuItem key={index} value={treatment.id}>
                {treatment.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </TableCell>
  </TableRow>
</TableHead>

                <TableBody>
                    {Object.keys(controls).length===0 && 
                    <TableRow
                        key={0}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                        <TableCell component="th" scope="row">
                            No controls found
                        </TableCell>
                    </TableRow>
                    }

                    {controls.map((row:any, index:number)=>{
                    return <TableRow
                        key={index}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, verticalAlign: 'top' }}
                    >
                        <TableCell component="th" scope="row">
                            <strong>
                                <Link style={{color:'#EE8E29'}} to={`/assessment/questionnaire/${row.id_category}/${row.id_subcategory}/${row.id_control}`}>{row.id_control}</Link>
                            </strong> ({row.id_function} / {row.id_category} / {row.id_subcategory})
                            <Box sx={{fontSize:12}}>{row.control_description}</Box>
                        </TableCell>
                        <TableCell>
                            {!row.coverage_notes && <Box sx={{padding: '16px'}}>{row.coverage?.name}</Box>}
                            {row.coverage_notes &&
                            <Accordion>
                                <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="coverage-accordion"
                                id="coverage-accordion"
                                >
                                {row.coverage?.name}
                                </AccordionSummary>
                                <AccordionDetails sx={{fontSize:12, padding:'0px 16px'}}>
                                {row.coverage_notes}
                                </AccordionDetails>
                            </Accordion>}
                        </TableCell>
                        <TableCell>
                            {!row.maturity_notes && <Box sx={{padding: '16px'}}>{row.maturity?.name}</Box>}
                            {row.maturity_notes &&
                            <Accordion>
                                <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="maturity-accordion"
                                id="maturity-accordion"
                                >
                                {row.maturity?.name}
                                </AccordionSummary>
                                <AccordionDetails sx={{fontSize:12, padding:'0px 16px'}}>
                                {row.maturity_notes}
                                </AccordionDetails>
                            </Accordion>}

                        </TableCell>
                        <TableCell>
                        <div>
                        <FormControl sx={{ m: 1, width: 300 }}>
                            <InputLabel id="treatments-chip-label">{t('Treatments')}</InputLabel>
                            <Select
                            
                            disabled={strategy.approved===1 ? true : false}
                            labelId="treatments-chip-label"
                            name={row.id_control}
                            multiple
                            value={treatments[row.id_control] ?? []}
                            onChange={handleChange}
                            input={<OutlinedInput id="select-multiple-chip" label="Treatments" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value, index) => (
                                    <Chip key={index} label={value} />
                                ))}
                                </Box>
                            )}
                            >
                                {treatmentsList.map((treatment:any, index:number) => (
                                <MenuItem
                                key={index}
                                value={treatment.id+"-"+treatment.name}
                                >
                                <Checkbox checked={treatments[row.id_control].includes(treatment.id+"-"+treatment.name)} />
                                {treatment.id+"-"+treatment.name}
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                        </div>


                        </TableCell>
                    </TableRow>
                    })}
                </TableBody>
            </Table>
            </TableContainer>

        </Box>
        </Paper>}

        </Box>
      </AppLayout>
    )
}

export default Strategy;